import { getStyles } from "./styles";
import type { BannerCategory, BannerConfig, CookieCategory, DetectedCookie } from "./types";

export interface BannerCallbacks {
	onAcceptAll(): void;
	onRejectAll(): void;
	onCustom(choices: Record<CookieCategory, boolean>): void;
	/** Fired whenever the banner transitions visible → hidden. */
	onHide?(): void;
}

const POWERED_BY_LABEL: Record<"ro" | "en", string> = {
	ro: "Soluție oferită de",
	en: "Powered by",
};
const POWERED_BY_URL = "https://fewcookies.com?utm_source=widget&utm_medium=banner";

/**
 * Premium tabbed consent banner. Three layout families share the same tabbed
 * panel (Consent / Details / About):
 * - modal: centered dialog with overlay
 * - card-left / card-right: 384px corner card, no overlay
 * - bar-bottom / bar-top: full-width compact bar; "customize" expands the
 *   tabbed panel inline
 */
export class Banner {
	private config: BannerConfig;
	private detectedCookies: DetectedCookie[];
	private host: HTMLElement;
	private shadow: ShadowRoot;
	private bannerEl: HTMLElement | null = null;
	private overlayEl: HTMLElement | null = null;
	private panelEl: HTMLElement | null = null;
	private acceptAllBtn: HTMLButtonElement | null = null;
	private callbacks: BannerCallbacks;
	private toggles: Map<CookieCategory, HTMLInputElement> = new Map();
	private tabButtons: HTMLButtonElement[] = [];
	private tabPanels: HTMLElement[] = [];
	private visible = false;

	constructor(
		config: BannerConfig,
		callbacks: BannerCallbacks,
		detectedCookies: DetectedCookie[] = [],
	) {
		this.config = config;
		this.callbacks = callbacks;
		this.detectedCookies = detectedCookies;

		this.host = document.createElement("div");
		this.host.id = "cookieguard-widget";
		// Fixed, pointer-transparent host: the banner/overlay children are
		// themselves position:fixed, so they anchor to the viewport regardless
		// of the host box, and only they receive pointer events.
		this.host.style.cssText =
			"position:fixed;bottom:0;left:0;width:100%;z-index:2147483647;pointer-events:none;";
		this.shadow = this.host.attachShadow({ mode: "closed" });

		this.render();
		document.body.appendChild(this.host);
	}

	/**
	 * Handle to the closed shadow root, for tests only. Safe: the Banner
	 * instance itself is module-private and never exposed on `window`.
	 */
	get rootForTesting(): ShadowRoot {
		return this.shadow;
	}

	show(): void {
		if (this.visible) return;
		this.visible = true;

		if (this.overlayEl) {
			this.overlayEl.classList.remove("cg-hidden");
			void this.overlayEl.offsetHeight;
			this.overlayEl.classList.add("cg-visible");
		}

		if (this.bannerEl) {
			this.bannerEl.classList.remove("cg-hidden");
			void this.bannerEl.offsetHeight;
			this.bannerEl.classList.add("cg-visible");
		}

		// Move focus inside every layout so the dialog is announced immediately.
		this.acceptAllBtn?.focus();
	}

	hide(): void {
		if (!this.visible) return;
		this.visible = false;

		this.overlayEl?.classList.remove("cg-visible");
		this.bannerEl?.classList.remove("cg-visible");

		// Wait for the 250ms exit animation to complete. onHide fires only once
		// the banner is actually gone, so the reopen button never overlaps a
		// still-fading banner (and never fires if show() was called meanwhile).
		setTimeout(() => {
			if (!this.visible) {
				this.overlayEl?.classList.add("cg-hidden");
				this.bannerEl?.classList.add("cg-hidden");
				// Collapse the inline bar panel and reset to the first tab
				this.panelEl?.classList.remove("cg-open");
				this.bannerEl?.classList.remove("cg-expanded");
				this.selectTab(0, false);
				this.callbacks.onHide?.();
			}
		}, 300);
	}

	destroy(): void {
		this.host.remove();
	}

	setToggles(choices: Record<CookieCategory, boolean>): void {
		for (const [cat, input] of this.toggles) {
			if (cat !== "necessary") {
				input.checked = choices[cat] ?? false;
			}
		}
	}

	// ── Rendering ────────────────────────────────────────────────────────────

	private render(): void {
		const { config } = this;
		const style = document.createElement("style");
		style.textContent = getStyles(config);

		const isModal = config.layout === "modal";
		const isBar = config.layout === "bar-bottom" || config.layout === "bar-top";

		this.overlayEl = document.createElement("div");
		this.overlayEl.className = "cg-overlay cg-hidden";

		this.bannerEl = document.createElement("div");
		this.bannerEl.className = `cg-banner cg-layout-${config.layout} cg-hidden`;
		this.bannerEl.setAttribute("role", "dialog");
		this.bannerEl.setAttribute("aria-label", config.texts.title);
		this.bannerEl.setAttribute("aria-modal", "true");
		this.bannerEl.addEventListener("keydown", (event) => this.onDialogKeydown(event));

		if (isBar) {
			this.renderBar(this.bannerEl);
		} else {
			this.bannerEl.appendChild(this.buildHeader());
			this.bannerEl.appendChild(this.buildTabbedPanel());
		}

		if (isModal) {
			this.shadow.append(style, this.overlayEl, this.bannerEl);
		} else {
			this.shadow.append(style, this.bannerEl);
		}
	}

	private renderBar(root: HTMLElement): void {
		const { texts } = this.config;

		const row = document.createElement("div");
		row.className = "cg-bar-row";

		const text = document.createElement("div");
		text.className = "cg-bar-text";
		text.appendChild(this.buildHeader());
		const desc = document.createElement("p");
		desc.className = "cg-description";
		desc.textContent = texts.description;
		text.appendChild(desc);

		const actions = document.createElement("div");
		actions.className = "cg-bar-actions";

		const customizeBtn = this.createButton(texts.customize, "cg-btn-outline", () => {
			const open = this.panelEl?.classList.toggle("cg-open") ?? false;
			this.bannerEl?.classList.toggle("cg-expanded", open);
			customizeBtn.setAttribute("aria-expanded", String(open));
		});
		customizeBtn.setAttribute("aria-expanded", "false");

		const rejectBtn = this.createButton(texts.rejectAll, "cg-btn-outline", () => {
			this.callbacks.onRejectAll();
			this.hide();
		});

		this.acceptAllBtn = this.createButton(texts.acceptAll, "cg-btn-primary", () => {
			this.callbacks.onAcceptAll();
			this.hide();
		});

		actions.append(customizeBtn, rejectBtn, this.acceptAllBtn);
		row.append(text, actions);

		this.panelEl = this.buildTabbedPanel();
		this.panelEl.classList.add("cg-panel");

		root.append(row, this.panelEl);
		if (this.config.branding.poweredBy) {
			root.appendChild(this.buildPoweredBy());
		}
	}

	private buildHeader(): HTMLElement {
		const { branding, texts } = this.config;
		const header = document.createElement("div");
		header.className = "cg-header";

		if (branding.logo) {
			const logo = document.createElement("img");
			logo.className = "cg-logo";
			logo.src = branding.logo;
			logo.alt = branding.logoAlt ?? "";
			header.appendChild(logo);
		}

		const title = document.createElement("div");
		title.className = "cg-title";
		title.textContent = texts.title;
		header.appendChild(title);

		return header;
	}

	/** The shared tabbed panel: Consent / Details / About. */
	private buildTabbedPanel(): HTMLElement {
		const { texts } = this.config;
		const isBar = this.config.layout === "bar-bottom" || this.config.layout === "bar-top";
		const container = document.createElement("div");

		const tablist = document.createElement("div");
		tablist.className = "cg-tabs";
		tablist.setAttribute("role", "tablist");
		tablist.setAttribute("aria-label", texts.title);

		// The bar layout repeats the description in its compact row, so the
		// consent tab keeps it only for modal/card.
		const entries: Array<{ label: string; panel: HTMLElement }> = [
			{ label: texts.tabConsent, panel: this.buildConsentPanel(!isBar) },
			{ label: texts.tabDetails, panel: this.buildDetailsPanel() },
			{ label: texts.tabAbout, panel: this.buildAboutPanel() },
		];

		this.tabButtons = [];
		this.tabPanels = [];

		entries.forEach(({ label, panel }, i) => {
			const tab = document.createElement("button");
			tab.type = "button";
			tab.className = `cg-tab${i === 0 ? " cg-active" : ""}`;
			tab.id = `cg-tab-${i}`;
			tab.textContent = label;
			tab.setAttribute("role", "tab");
			tab.setAttribute("aria-selected", i === 0 ? "true" : "false");
			tab.setAttribute("aria-controls", `cg-tabpanel-${i}`);
			tab.tabIndex = i === 0 ? 0 : -1;
			tab.addEventListener("click", () => this.selectTab(i, false));
			tab.addEventListener("keydown", (e) => this.onTabKeydown(e, i));
			tablist.appendChild(tab);
			this.tabButtons.push(tab);

			panel.id = `cg-tabpanel-${i}`;
			panel.setAttribute("role", "tabpanel");
			panel.setAttribute("aria-labelledby", `cg-tab-${i}`);
			if (i !== 0) panel.classList.add("cg-hidden");
			this.tabPanels.push(panel);
		});

		container.appendChild(tablist);
		for (const panel of this.tabPanels) container.appendChild(panel);

		// Actions live OUTSIDE the panels so they persist across tab switches.
		container.appendChild(this.buildActions(isBar));
		if (!isBar && this.config.branding.poweredBy) {
			container.appendChild(this.buildPoweredBy());
		}
		return container;
	}

	private onTabKeydown(e: KeyboardEvent, index: number): void {
		const count = this.tabButtons.length;
		let next = -1;
		if (e.key === "ArrowRight") next = (index + 1) % count;
		else if (e.key === "ArrowLeft") next = (index - 1 + count) % count;
		else if (e.key === "Home") next = 0;
		else if (e.key === "End") next = count - 1;
		if (next === -1) return;
		e.preventDefault();
		this.selectTab(next, true);
	}

	private selectTab(index: number, focus: boolean): void {
		this.tabButtons.forEach((tab, i) => {
			const active = i === index;
			tab.classList.toggle("cg-active", active);
			tab.setAttribute("aria-selected", String(active));
			tab.tabIndex = active ? 0 : -1;
		});
		this.tabPanels.forEach((panel, i) => {
			panel.classList.toggle("cg-hidden", i !== index);
		});
		if (focus) this.tabButtons[index]?.focus();
	}

	private onDialogKeydown(event: KeyboardEvent): void {
		if (!this.visible || event.key !== "Tab" || !this.bannerEl) return;

		const focusable = Array.from(
			this.bannerEl.querySelectorAll<HTMLElement>(
				'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]',
			),
		).filter(
			(element) =>
				element.tabIndex >= 0 &&
				!element.closest(".cg-hidden") &&
				!element.closest(".cg-panel:not(.cg-open)"),
		);

		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		if (!first || !last) return;

		const active = this.shadow.activeElement;
		if (event.shiftKey && (active === first || !this.bannerEl.contains(active))) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && (active === last || !this.bannerEl.contains(active))) {
			event.preventDefault();
			first.focus();
		}
	}

	// ── Consent tab ──────────────────────────────────────────────────────────

	private buildConsentPanel(withDescription: boolean): HTMLElement {
		const { texts, categories } = this.config;
		const panel = document.createElement("div");
		panel.className = "cg-consent";

		if (withDescription) {
			const desc = document.createElement("p");
			desc.className = "cg-description";
			desc.textContent = texts.description;
			panel.appendChild(desc);
		}

		const readMore = this.buildReadMoreLink();
		if (readMore) panel.appendChild(readMore);

		const selectLabel = document.createElement("div");
		selectLabel.className = "cg-select-label";
		selectLabel.textContent = texts.selectLabel;
		panel.appendChild(selectLabel);

		const grid = document.createElement("div");
		grid.className = "cg-cat-grid";
		for (const category of categories) {
			grid.appendChild(this.createCategoryCell(category));
		}
		panel.appendChild(grid);

		return panel;
	}

	/**
	 * Persistent buttons row rendered BELOW the tab panels so consent actions
	 * stay reachable from every tab. Modal/card get the full three-button row;
	 * the bar keeps reject/accept in its always-visible compact row, so its
	 * expanded panel only needs the save-selection action.
	 */
	private buildActions(saveOnly: boolean): HTMLElement {
		const { texts } = this.config;
		const actions = document.createElement("div");
		actions.className = `cg-actions${saveOnly ? " cg-actions-save" : ""}`;

		const saveBtn = this.createButton(texts.save, saveOnly ? "cg-btn-primary" : "cg-btn-outline", () => {
			this.callbacks.onCustom(this.getChoices());
			this.hide();
		});

		if (saveOnly) {
			actions.append(saveBtn);
			return actions;
		}

		const rejectBtn = this.createButton(texts.rejectAll, "cg-btn-outline", () => {
			this.callbacks.onRejectAll();
			this.hide();
		});

		const acceptBtn = this.createButton(texts.acceptAll, "cg-btn-primary", () => {
			this.callbacks.onAcceptAll();
			this.hide();
		});
		// Modal/card focus target; the bar keeps its compact-row accept button.
		if (!this.acceptAllBtn) this.acceptAllBtn = acceptBtn;

		actions.append(rejectBtn, saveBtn, acceptBtn);
		return actions;
	}

	private buildReadMoreLink(): HTMLElement | null {
		const { privacyPolicyUrl, texts, language } = this.config;
		if (!privacyPolicyUrl) return null;
		const link = document.createElement("a");
		link.className = "cg-readmore";
		link.href = privacyPolicyUrl;
		link.target = "_blank";
		link.rel = "noopener noreferrer";
		link.textContent = `${texts.readMore}${language === "ro" ? " aici" : " here"}`;
		return link;
	}

	private buildPoweredBy(): HTMLElement {
		const powered = document.createElement("div");
		powered.className = "cg-powered";
		powered.appendChild(
			document.createTextNode(`${POWERED_BY_LABEL[this.config.language] ?? POWERED_BY_LABEL.ro} `),
		);
		const link = document.createElement("a");
		link.href = POWERED_BY_URL;
		link.target = "_blank";
		link.rel = "noopener noreferrer";
		link.textContent = "fewcookies.com";
		powered.appendChild(link);
		return powered;
	}

	private createCategoryCell(category: BannerCategory): HTMLElement {
		const cell = document.createElement("div");
		cell.className = "cg-cat-cell";

		const name = document.createElement("div");
		name.className = "cg-cat-name";
		name.textContent = category.label;

		const toggle = document.createElement("label");
		toggle.className = "cg-toggle";

		const input = document.createElement("input");
		input.type = "checkbox";
		input.checked = category.required; // Required categories start checked
		input.disabled = category.required; // Required categories can't be unchecked
		input.setAttribute("aria-label", category.label);

		const slider = document.createElement("span");
		slider.className = "cg-toggle-slider";

		toggle.append(input, slider);
		cell.append(name, toggle);

		this.toggles.set(category.key, input);
		return cell;
	}

	// ── Details tab ──────────────────────────────────────────────────────────

	private buildDetailsPanel(): HTMLElement {
		const panel = document.createElement("div");
		panel.className = "cg-details";

		for (const category of this.config.categories) {
			const cookies = this.detectedCookies.filter((c) => c.category === category.key);
			if (this.detectedCookies.length > 0 && cookies.length === 0) continue;

			const group = document.createElement("div");
			group.className = "cg-detail-group";

			const heading = document.createElement("div");
			heading.className = "cg-detail-heading";
			heading.textContent = category.label;
			group.appendChild(heading);

			if (cookies.length > 0) {
				for (const cookie of cookies) {
					const row = document.createElement("div");
					row.className = "cg-cookie";

					const name = document.createElement("div");
					name.className = "cg-cookie-name";
					name.textContent = cookie.name;

					const desc = document.createElement("div");
					desc.className = "cg-cookie-desc";
					desc.textContent = cookie.description;

					row.append(name, desc);
					group.appendChild(row);
				}
			} else {
				const desc = document.createElement("div");
				desc.className = "cg-detail-group-desc";
				desc.textContent = category.description;
				group.appendChild(desc);
			}

			panel.appendChild(group);
		}

		return panel;
	}

	// ── About tab ────────────────────────────────────────────────────────────

	private buildAboutPanel(): HTMLElement {
		const { texts, categories } = this.config;
		const panel = document.createElement("div");
		panel.className = "cg-about";

		for (const paragraph of texts.about.split(/\n\n+/)) {
			const trimmed = paragraph.trim();
			if (!trimmed) continue;
			const p = document.createElement("p");
			p.textContent = trimmed;
			panel.appendChild(p);
		}

		const list = document.createElement("div");
		list.className = "cg-about-cats";
		for (const category of categories) {
			const row = document.createElement("div");
			row.className = "cg-about-cat";

			const label = document.createElement("div");
			label.className = "cg-about-cat-label";
			label.textContent = category.label;

			const desc = document.createElement("div");
			desc.className = "cg-about-cat-desc";
			desc.textContent = category.description;

			row.append(label, desc);
			list.appendChild(row);
		}
		panel.appendChild(list);

		const readMore = this.buildReadMoreLink();
		if (readMore) panel.appendChild(readMore);

		return panel;
	}

	// ── Helpers ──────────────────────────────────────────────────────────────

	private createButton(text: string, className: string, onClick: () => void): HTMLButtonElement {
		const btn = document.createElement("button");
		btn.className = `cg-btn ${className}`;
		btn.textContent = text;
		btn.type = "button";
		btn.addEventListener("click", onClick);
		return btn;
	}

	private getChoices(): Record<CookieCategory, boolean> {
		return {
			necessary: true,
			functional: this.toggles.get("functional")?.checked ?? false,
			analytics: this.toggles.get("analytics")?.checked ?? false,
			marketing: this.toggles.get("marketing")?.checked ?? false,
		};
	}
}
